import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LienPriorityType } from 'constants/loanApplication';
import { lienPriorityOptions as allLienPriorityOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { openDialogAction } from 'store/global/actions';
import { getPropertiesAction, getUsedLienPrioritiesAction } from 'store/loanApplications/actions';
import {
  selectCommonInfoLoanApplication,
  selectFinancialInfoOwnersOptions,
  selectIsChattelLoanPurposeFromApplication,
  selectIsRefinanceLoanPurposeFromApplication,
  selectLoanApplicationPropertiesByLiabilityId,
} from 'store/loanApplications/selectors';
import { selectLiabilityLienPriorityOptions } from 'store/loanApplications/selectors/lienPriorities.selectors';

import { SectionContentPropTypes } from '../SectionContent.constants';

import CalculateMonthlyPaymentModal from './CalculateMonthlyPaymentModal';
import { accountTypesWithMortgageDetails, getConfig, getConfigFields } from './Liabilities.config';
import {
  checkShouldForcePaidOffAndNonCashOut,
  getChattelHomeLienDefaultLienPriority,
  normalizeForm,
} from './Liabilities.utils';

const defaultArray = [];
const defaultObject = {};

const Liabilities = ({
  allDeletedRecords = defaultArray,
  form,
  formDataRef,
  handleChange,
  isLoading,
  setInForm,
  setForm,
  validateField,
  validationErrors,
  isSaving = false,
  newRecords,
  recordId,
  setRecordConfig,
  initialFormValues,
  data = defaultObject,
  isDeleted,
  applicationId,
  setValidationErrors,
  isNewRecord,
  borrowerId,
  pendingChanges,
  disabledPendingChangesHighlight,
  isOwnersClicked = false,
  isMortgageClicked,
}) => {
  const initialFormValuesRef = useRef({});

  const recordForm = form[recordId];
  const configFields = getConfigFields({ recordId });
  const { value: propertyId, isSubjectProperty } = recordForm?.property || {};

  const propertiesOptions = useSelector(
    selectLoanApplicationPropertiesByLiabilityId(recordId),
    _.isEqual,
  );
  const ownersOptions = useSelector(selectFinancialInfoOwnersOptions, _.isEqual);
  const isRefinanceFlow = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isChattelLoan = useSelector(selectIsChattelLoanPurposeFromApplication);
  const commonInfoLoanApplication = useSelector(selectCommonInfoLoanApplication);

  const refinancePurpose = commonInfoLoanApplication?.refinancePurpose;
  const lienPriorityOptions = useSelector(
    selectLiabilityLienPriorityOptions(isSubjectProperty),
    _.isEqual,
  );

  const isPaidOff = recordForm?.toBePaidOffAtOrBeforeClosing;
  const isExcludePayment = recordForm?.excludePayment;
  const isSubordinate = recordForm?.isSubordinate;
  const paymentExclusionReason = recordForm?.paymentExclusionReason?.value;

  const lienPriorityTypeOptions = useMemo(() => {
    if (isMortgageClicked) {
      return lienPriorityOptions;
    }

    return (isPaidOff || isExcludePayment ? allLienPriorityOptions : lienPriorityOptions).filter(
      ({ value }) => {
        const availableLiabilities = [
          ...Object.keys(data),
          ...newRecords.map(({ id }) => id),
        ].filter(id => id !== recordId && !allDeletedRecords.includes(id));

        return (
          !propertyId ||
          value === LienPriorityType.OTHER ||
          !availableLiabilities.some(recordId => {
            const form = formDataRef.current;

            const isRecordInForm = form[recordId];

            const propertyValue = isRecordInForm
              ? form[recordId].property?.value
              : data[recordId]?.property;
            const lienPriorityType = isRecordInForm
              ? form[recordId].lienPriorityType?.value
              : data[recordId]?.lienPriorityType;

            return propertyValue === propertyId && value === lienPriorityType;
          })
        );
      },
    );
  }, [
    isMortgageClicked,
    isPaidOff,
    isExcludePayment,
    lienPriorityOptions,
    data,
    newRecords,
    propertyId,
    recordId,
    allDeletedRecords,
    formDataRef,
  ]);

  const liabilityType = recordForm?.type?.value;
  const accountType = recordForm?.accountType?.value;
  const loanType = recordForm?.loanType?.value;
  const sourceOfFunds = recordForm?.sourceOfFunds?.value;
  const isHeloc = recordForm?.heloc;
  const unpaidBalance = recordForm?.unpaidBalance;
  const lienPriorityType = recordForm?.lienPriorityType?.value;

  const dispatch = useDispatch();

  const onCalculateMonthlyPayment = useCallback(() => {
    const onSubmit = val => setInForm(`${recordId}.monthlyPayment`, val);

    dispatch(
      openDialogAction({
        header: 'Monthly Payment',
        isRestyled: true,
        hasScrollY: false,
        body: dialogProps => (
          <CalculateMonthlyPaymentModal
            unpaidBalance={unpaidBalance || 0}
            onSubmit={onSubmit}
            {...dialogProps}
          />
        ),
      }),
    );
  }, [dispatch, unpaidBalance, setInForm, recordId]);

  const shouldForcePaidOffAndNonCashOut = checkShouldForcePaidOffAndNonCashOut({
    isRefinanceFlow,
    isSubjectProperty,
    lienPriorityType,
    isSubordinate,
    isExcludePayment,
    refinancePurpose,
  });

  const config = useMemo(
    () =>
      getConfig({
        recordId,
        liabilityType,
        accountType,
        loanType,
        sourceOfFunds,
        isHeloc,
        isPaidOff,
        isExcludePayment,
        paymentExclusionReason,
        propertiesOptions,
        lienPriorityTypeOptions,
        lienPriorityType,
        ownersOptions,
        isNewRecord,
        isRefinanceFlow,
        isOwnersClicked,
        isMortgageClicked,
        isSubjectProperty,
        onCalculateMonthlyPayment,
        shouldForcePaidOffAndNonCashOut,
        isChattelLoan,
      }),
    [
      recordId,
      isOwnersClicked,
      liabilityType,
      accountType,
      loanType,
      sourceOfFunds,
      isHeloc,
      isPaidOff,
      isExcludePayment,
      paymentExclusionReason,
      propertiesOptions,
      lienPriorityTypeOptions,
      lienPriorityType,
      ownersOptions,
      isNewRecord,
      isRefinanceFlow,
      isMortgageClicked,
      isSubjectProperty,
      onCalculateMonthlyPayment,
      shouldForcePaidOffAndNonCashOut,
      isChattelLoan,
    ],
  );

  useEffect(() => {
    if (isDeleted) {
      return;
    }

    setRecordConfig(config, recordId);
  }, [config, setRecordConfig, isDeleted, recordId]);

  useEffectSafeStrictMode(() => {
    dispatch(getPropertiesAction({ applicationId }));
    dispatch(getUsedLienPrioritiesAction({ applicationId }));
  }, [dispatch, applicationId]);

  useEffect(() => {
    initialFormValuesRef.current = initialFormValues;
  }, [initialFormValues]);

  useEffect(() => {
    setForm(form =>
      normalizeForm({
        form,
        recordId,
        liabilityType,
        accountType,
        initialFormValuesRef,
        borrowerId,
        ownersOptions,
        lienPriorityTypeOptions,
        setValidationErrors,
        isNewRecord,
        isRefinanceFlow,
        isChattelLoan,
      }),
    );
  }, [
    data,
    setForm,
    recordId,
    initialFormValues,
    liabilityType,
    accountType,
    borrowerId,
    ownersOptions,
    lienPriorityTypeOptions,
    setValidationErrors,
    isNewRecord,
    isRefinanceFlow,
    isChattelLoan,
  ]);

  useEffect(() => {
    const defaultLienPriority = getChattelHomeLienDefaultLienPriority({
      isChattelLoan,
      accountType,
      isSubjectProperty,
      lienPriorityType,
      lienPriorityTypeOptions,
    });

    if (defaultLienPriority) {
      setInForm(configFields.lienPriorityType.nameInModel, defaultLienPriority, true);
    }
  }, [
    isChattelLoan,
    accountType,
    isSubjectProperty,
    lienPriorityType,
    lienPriorityTypeOptions,
    configFields.lienPriorityType.nameInModel,
    setInForm,
  ]);

  useEffect(() => {
    if (
      !accountTypesWithMortgageDetails.includes(accountType) ||
      lienPriorityType !== LienPriorityType.FIRST ||
      !recordForm?.isSubordinate
    ) {
      return;
    }

    setInForm(configFields.isSubordinate.nameInModel, false, true);
  }, [
    accountType,
    lienPriorityType,
    recordForm?.isSubordinate,
    configFields.isSubordinate.nameInModel,
    setInForm,
  ]);

  return (
    <FormGenerator
      isNestedForm
      config={config}
      disabledPendingChangesHighlight={disabledPendingChangesHighlight}
      form={form}
      formName={FormNames.LIABILITIES}
      handleChange={handleChange}
      isLoading={isSaving || isLoading}
      numberOfColumns={4}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

Liabilities.propTypes = {
  ..._.omit(SectionContentPropTypes, [
    'schema',
    'setSchema',
    'setIsLoading',
    'staticValidateField',
  ]),
  data: PropTypes.object,
  isSaving: PropTypes.bool,
  isOwnersClicked: PropTypes.bool,
  allDeletedRecords: PropTypes.array,
};

export default React.memo(Liabilities, _.isEqual);
